import { useState, type CSSProperties, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { Bild } from '../api/client';
import { bildSitzungErneuern } from '../lib/bildSitzung';
import { kategorieFarbe } from '../lib/rezept';
import { Icon, IconMehr, type IconName } from './Icon';
import s from './karten.module.css';

/**
 * Rezept-Illustration mit Platzhalter: die Flaeche traegt immer die Kategoriefarbe, das Bild blendet
 * erst nach onload ein (dur_slow). Mit bildStatus "wartet"/"in_arbeit" steht dezent "Bild entsteht"
 * darauf (Mack B8, color_ink_muted ist auf allen Kategorieflaechen zulaessig, GL-023 §12.2). "fehler": nur die Farbe.
 * Kleine Vorschaubilder (60 px) geben keinen bildStatus mit.
 */
export function RezeptBild({
  bild,
  kategorie,
  sizes,
  prioritaet = false,
  className,
  stil,
  bildStatus,
}: {
  bild: Bild | null | undefined;
  kategorie: string | null | undefined;
  bildStatus?: string | null;
  /** CSS sizes, z. B. "(max-width: 600px) 50vw, 290px" */
  sizes: string;
  prioritaet?: boolean;
  className?: string;
  stil?: CSSProperties;
}) {
  const [geladen, setGeladen] = useState(false);
  // Abgewiesen (Bild-Cookie abgelaufen): Cookie erneuern und genau einmal neu laden
  const [versuch, setVersuch] = useState(0);
  const entsteht = !bild && (bildStatus === 'wartet' || bildStatus === 'in_arbeit');
  return (
    <span className={`${s.bildFlaeche} ${className ?? ''}`} style={{ background: kategorieFarbe(kategorie), ...stil }}>
      {entsteht && (
        <span className={s.bildEntsteht}>
          <Icon name="pinsel" groesse={18} strich={1.8} />
          Bild entsteht
        </span>
      )}
      {bild && (
        <img
          key={versuch}
          onError={() => {
            if (versuch === 0 && navigator.onLine !== false) void bildSitzungErneuern().then((ok) => ok && setVersuch(1));
          }}
          src={bild.mittel}
          srcSet={`${bild.klein} 400w, ${bild.mittel} 800w, ${bild.gross} 1200w`}
          sizes={sizes}
          alt=""
          loading={prioritaet ? 'eager' : 'lazy'}
          decoding="async"
          className={s.bild}
          data-geladen={geladen}
          onLoad={() => setGeladen(true)}
          ref={(el) => {
            // aus dem Cache geladene Bilder feuern onLoad manchmal vor dem Einhaengen
            if (el?.complete && el.naturalWidth > 0 && !geladen) setGeladen(true);
          }}
        />
      )}
    </span>
  );
}

const HERKUNFT: Record<string, { text: string; nora: boolean } | undefined> = {
  nora: { text: 'Von Nora', nora: true },
  import: { text: 'Importiert', nora: false },
};

export interface KartenDaten {
  slug: string;
  name: string;
  kategorie: string | null;
  herkunft: string;
  bild: Bild | null;
  bild_status?: string;
}

/**
 * Rezeptkarte im Raster (GL-023 §9.6): 190-px-Bildflaeche als Link, Herkunfts-Badge, weisses Titel-Label,
 * darunter Metazeile mit Gesamtzeit und kcal und das Drei-Punkte-Menue.
 * index steuert den Einblende-Versatz (40 ms je Karte, hoechstens 8).
 */
export function RezeptKarte({
  rezept,
  meta,
  onMenue,
  index = 0,
  prioritaet = false,
}: {
  rezept: KartenDaten;
  meta: ReactNode;
  onMenue?: () => void;
  index?: number;
  prioritaet?: boolean;
}) {
  const herkunft = HERKUNFT[rezept.herkunft];
  return (
    <article className={s.karte} style={{ '--i': Math.min(index, 8) } as CSSProperties}>
      <Link to={`/rezept/${rezept.slug}`} className={s.kartenLink}>
        <RezeptBild
          bild={rezept.bild}
          kategorie={rezept.kategorie}
          bildStatus={rezept.bild_status}
          sizes="(max-width: 600px) 50vw, 290px"
          prioritaet={prioritaet}
        />
        {herkunft && <span className={`${s.badge} ${herkunft.nora ? s.badgeNora : ''}`}>{herkunft.text}</span>}
        <span className={s.label}>
          <span className={s.titel}>{rezept.name}</span>
        </span>
      </Link>
      <div className={s.metaZeile}>
        <p className={s.meta}>{meta}</p>
        {onMenue && (
          <button type="button" className={s.menue} aria-label={`Weitere Aktionen für ${rezept.name}`} aria-haspopup="dialog" onClick={onMenue}>
            <IconMehr />
          </button>
        )}
      </div>
    </article>
  );
}

/** Kollektionskarte (Canvas R4): bis zu drei Bilder als Collage, Name im weissen Label, Anzahl darunter. */
export function KollektionKarte({
  an,
  name,
  anzahl,
  vorschau,
  automatisch,
  index = 0,
}: {
  an: string;
  name: string;
  anzahl: number;
  vorschau: ReadonlyArray<{ kategorie: string | null; bild: Bild | null }>;
  automatisch: boolean;
  index?: number;
}) {
  const kacheln = [vorschau[0], vorschau[1], vorschau[2]];
  return (
    <article className={s.karte} style={{ '--i': Math.min(index, 8) } as CSSProperties}>
      <Link to={an} className={`${s.kartenLink} ${s.kollektionLink}`}>
        <span aria-hidden="true" className={s.collage}>
          {kacheln.map((v, i) => (
            <RezeptBild
              key={i}
              bild={v?.bild}
              kategorie={v?.kategorie}
              sizes="(max-width: 600px) 25vw, 145px"
              className={i === 0 ? s.collageGross : i === 2 ? `${s.collageKlein} ${s.collageDritte}` : s.collageKlein}
              stil={v ? undefined : { background: 'var(--nb-color-surface-subtle)' }}
            />
          ))}
          {anzahl === 0 && (
            <span className={s.collageLeer}>
              <Icon name={automatisch ? 'merken' : 'kollektion'} groesse={24} />
            </span>
          )}
        </span>
        <span className={s.label}>
          <span className={s.titel}>{name}</span>
        </span>
      </Link>
      <p className={s.anzahl}>{anzahl === 1 ? '1 Rezept' : `${anzahl} Rezepte`}</p>
    </article>
  );
}

/** Zwei-Spalten-Raster fuer Karten. */
export function KartenRaster({ children }: { children: ReactNode }) {
  return (
    <div className={s.raster}>
      {children}
    </div>
  );
}

/** Banner "Rezept fotografieren" / "Rezept importieren" (GL-023 §9.9 Import-Banner). */
export function ImportBanner({ an, titel, text, icon = 'kamera' }: { an: string; titel: string; text: string; icon?: IconName }) {
  return (
    <Link to={an} className={s.banner}>
      <span aria-hidden="true" className={s.bannerKreis}>
        <Icon name={icon} groesse={22} />
      </span>
      <span className={s.bannerText}>
        <span className={s.bannerTitel}>{titel}</span>
        <span className={s.bannerUnter}>{text}</span>
      </span>
      <Icon name="chevronRechts" groesse={20} strich={2} />
    </Link>
  );
}
